export const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// MIME subtypes whose upper-cased form is not a friendly format label.
const MIME_FORMAT_LABELS: Record<string, string> = {
  'vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'msword': 'DOC',
  'quicktime': 'MOV',
  'x-msvideo': 'AVI',
  'mpeg': 'MP3',
  'mp3': 'MP3',
  'x-m4a': 'M4A',
  'm4a': 'M4A',
  'x-wav': 'WAV',
  'wave': 'WAV',
  'jpeg': 'JPG',
};

// Short display label for a file's format (e.g. "DOCX", "MP3"), derived from
// the MIME type with the file extension as fallback.
export const getFormatLabel = (mimeType?: string, fileName?: string): string => {
  if (mimeType && mimeType.includes('/')) {
    const subtype = mimeType.split('/')[1].toLowerCase();
    const mapped = MIME_FORMAT_LABELS[subtype];
    if (mapped) return mapped;
    let format = subtype.toUpperCase();
    if (format.startsWith('X-')) format = format.substring(2);
    // Raw MIME subtypes like the docx one are unreadable - fall back to the
    // extension when the subtype clearly isn't a short format name.
    if (format.length <= 4) return format;
  }
  // Only treat text after a dot as an extension - splitting on a name with no
  // dot returns the whole name, which would show up as the format.
  const dotIndex = fileName ? fileName.lastIndexOf('.') : -1;
  if (fileName && dotIndex > 0 && dotIndex < fileName.length - 1) {
    return fileName.slice(dotIndex + 1).toUpperCase();
  }
  return 'FILE';
};

// How a document-family file can be previewed in the browser. 'pdf', 'image'
// and 'docx' all open in the DocumentViewer; null means no in-browser preview
// (e.g. legacy binary .doc) and the file should be downloaded instead.
//
// MIME type is checked first and the filename is only a fallback. Resource
// labels have their extension stripped at upload time (see UploadQueueContext:
// `label: upload.file.name.replace(/\.[^/.]+$/, '')`), and the resource list
// renders that label as the file name - so a PDF usually arrives here as
// "Act 1 Score" with nothing to match on. Relying on the extension alone made
// every uploaded PDF download instead of opening, and suppressed its thumbnail.
export const getDocumentPreviewType = (
  fileName: string,
  mimeType?: string,
): 'pdf' | 'image' | 'docx' | null => {
  const mime = (mimeType || '').toLowerCase().split(';')[0].trim();

  if (mime) {
    if (mime === 'application/pdf') return 'pdf';
    if (mime.startsWith('image/')) return 'image';
    if (mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      return 'docx';
    }
    // Recognised but unpreviewable: legacy binary .doc has no browser renderer.
    if (mime === 'application/msword') return null;
  }

  const extension = fileName.toLowerCase().split('.').pop() || '';
  if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) return 'image';
  if (extension === 'pdf') return 'pdf';
  // Only Office Open XML converts client-side; the legacy binary .doc format
  // has no browser renderer, so it stays a download.
  if (extension === 'docx') return 'docx';
  return null;
};

/**
 * A PDF by MIME type or extension - the one file type that carries per-user
 * annotations, so a delete confirmation has to say those go with it.
 */
export const isPdfFile = (
  file: { name?: string; mimeType?: string } | null | undefined,
): boolean => !!file && getDocumentPreviewType(file.name ?? '', file.mimeType) === 'pdf';

const WORD_MIME_FORMATS: Record<string, 'docx' | 'doc'> = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/msword': 'doc',
};

/**
 * Which Word format a file is, if it is one - 'docx', 'doc' or null. The
 * server makes a PDF rendition of both, so opening either tries that first.
 * MIME type wins; then the storage path, which keeps its extension; then the
 * name, which for a resource usually has had its extension stripped.
 */
export const wordFormatOf = (
  file: { name?: string; mimeType?: string; storagePath?: string } | null | undefined,
): 'docx' | 'doc' | null => {
  if (!file) return null;
  const mime = (file.mimeType || '').toLowerCase().split(';')[0].trim();
  if (WORD_MIME_FORMATS[mime]) return WORD_MIME_FORMATS[mime];
  // A MIME type that names some other format settles it.
  if (mime === 'application/pdf' || /^(image|audio|video)\//.test(mime)) return null;
  for (const candidate of [file.storagePath, file.name]) {
    const extension = /\.([a-z0-9]{1,5})$/i.exec((candidate || '').split('?')[0])?.[1]?.toLowerCase();
    if (extension === 'docx' || extension === 'doc') return extension;
  }
  return null;
};
